import { lighten } from "polished";
import React, { useEffect } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { BorderButton } from "src/components/buttons/BorderButton";
import { FindFiltersQuery } from "src/components/filter/FindFilters";
import FacebookIcon from "src/components/icons/FacebookIcon";
import InstagramIcon from "src/components/icons/InstagramIcon";
import TwitterIcon from "src/components/icons/TwitterIcon";
import ItemCard from "src/components/items/item/ItemCard";
import Container from "src/components/layout/Container";
import { apiClient } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import { SwimAmericaDomains } from "src/utils/constants";
import helmet from "src/utils/helmet";
import {
  setListBackground,
  unsetListBackground,
} from "src/utils/setListBackground";

interface Props {}

export default function Home(props: Props) {
  const store = useStore();
  const history = useHistory();

  useEffect(() => {
    helmet.setPageTitle(
      store.organization && store.organization.id === "app" ? "Welcome" : "Home"
    );

    setListBackground();

    return () => {
      unsetListBackground();
    };
  }, []);

  async function explore() {
    if (store.organization.hasDepartments) {
      history.push("/explore");
      return;
    }

    const filters = await apiClient(FindFiltersQuery, {
      variables: {
        filter: "",
        groupWhere: {
          organizationId: store.organization.id,
        },
      },
    });

    const availableFilters = filters.findFilters;

    if (Object.keys(availableFilters).length === 0) {
      history.push("/find");
    } else {
      history.push("/explore");
    }
  }

  if (SwimAmericaDomains.includes(store.organization.subdomain)) {
    return <Redirect to="/search" />;
  }

  if (store.isApp) {
    if (store.user?.isSalesOrAbove) {
      return <Redirect to="/super-admin" />;
    }

    if (store.user) {
      return <Redirect to="/account" />;
    }
  }

  if (store.user) {
    if (store.user.isAdminOrAbove) {
      return <Redirect to="/admin" />;
    } else {
      return <Redirect to="/account" />;
    }
  }

  const description = store.organization?.description?.replace(
    /(?:\r\n|\r|\n)/g,
    "<br />"
  );

  return (
    <React.Fragment>
      {store.organization &&
        store.organization.logo &&
        store.organization.logo.url && (
          <div className="-mb-12 border-b border-borderGray bg-white pb-12">
            <Container>
              <p className="pt-2 text-center">
                <img
                  className="max-h-72 rounded"
                  src={store.organization.logo.url}
                  alt="Organization Logo"
                />
              </p>
            </Container>
          </div>
        )}
      <div className="px-4">
        <div className="mx-auto max-w-lg pb-14 pt-4">
          <ItemCard>
            <h3 className="mb-2">Get started!</h3>
            <p className="text-empty">
              Explore our offerings and get registered!
            </p>
            {store.organization && (
              <BorderButton
                type="submit"
                onClick={explore}
                className="sm:w-full"
              >
                Explore
              </BorderButton>
            )}
          </ItemCard>
          {description && (
            <div>
              <h3 className="mb-2">About us</h3>
              <div
                className="mb-4"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          )}

          {store.organization.facebook && (
            <a
              href={store.organization.facebook}
              rel="nofollow noreferrer"
              target="_blank"
              css={{
                color: store.theme.orgColor,
                "&:hover": {
                  color: lighten(0.04, store.theme.orgColor),
                },
              }}
              className="inline-block pr-6"
            >
              <FacebookIcon className="h-9 w-9" />
            </a>
          )}

          {store.organization.instagram && (
            <a
              href={store.organization.instagram}
              rel="nofollow noreferrer"
              target="_blank"
              css={{
                color: store.theme.orgColor,
                "&:hover": {
                  color: lighten(0.04, store.theme.orgColor),
                },
              }}
              className="inline-block pr-6"
            >
              <InstagramIcon className="h-9 w-9" />
            </a>
          )}

          {store.organization.twitter && (
            <a
              href={store.organization.twitter}
              rel="nofollow noreferrer"
              target="_blank"
              css={{
                color: store.theme.orgColor,
                "&:hover": {
                  color: lighten(0.04, store.theme.orgColor),
                },
              }}
              className="inline-block pr-6"
            >
              <TwitterIcon className="h-9 w-9" />
            </a>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}
